import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContextType, Theme, ViewMode, HighlightableItem, NavigationItem, DfrData, FmeaItem, PDiagramData, User, BotConfig, Customer, CustomerRequirement, ProjectMetadata, ProjectContextData, PbsRevisionLevel, PbsItemRevision, ECNRecord, IterationRecord, BomItem, Product, ProductFeature } from '../types';
import { MASTER_FUNCTIONS, functionColorMap } from '../data/mockData';
import { validateBeforeSave, normalizeProjectName, extractProjectName } from '../utils/validation';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import { downloadCSVWithAudit, wrapWithAuditMetadata, ExportAuditMetadata } from '../utils/exportUtils';
import { API_BASE_URL } from '../utils/apiConfig';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';


// Local implementation of missing services
// const fetchUsers = async (): Promise<User[]> => MOCK_USERS; (Unused)
// MOCK_USERS removed for security

// NOTE: Initial Data is now EMPTY. We strictly rely on Backend/Firestore.
const initialData: DfrData = {
    fmeaItems: [],
    bomItems: [],
    pDiagramList: [],
    dvprTests: [],
    controls: [],
    customers: [],
    customerRequirements: [],
    products: [],
    productFeatures: [],
    botConfigs: [],
    // Optional arrays initialized to empty for safety
    iterationHistory: [],
    pbsRevisions: [],
    pbsItems: [],
    ecnLog: [],
    // SIPOC Multi-Epoch details
    requirementChain: [],
    opalInsight: {},
    derivedFunctions: [],
    auditReportMd: "",
    systemPiScore: 0.0,
    performanceMetrics: [],
    cadqueryScript: "",
    shapes: [],
    contacts: []
};


/*
const debounce = <T extends (...args: unknown[]) => unknown>(func: T, wait: number): (...args: Parameters<T>) => void => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

const saveToLocalStorage = (key: string, data: unknown): void => {
    localStorage.setItem(key, JSON.stringify(data));
};
*/

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>('light');
    const [viewMode, setViewModeState] = useState<ViewMode>('advanced');
    const [navigationHistory, setNavigationHistory] = useState<NavigationItem[]>([]);
    const [highlightedItem, setHighlightedItem] = useState<HighlightableItem | null>(null);
    const [isHelpOpen, setHelpOpen] = useState<boolean>(false);

    // Auth State - Users come from Firebase Auth (Google Sign-In)
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [users, setUsers] = useState<User[]>([]); // Empty - populated from Firebase Auth only
    const [isAuthLoading, setIsAuthLoading] = useState(true); // Track Firebase auth loading state

    // Data State - EMPTY until loaded from Firebase backend
    // No mock data is used - ensures we verify Firebase is actually connected
    const [allData, setAllData] = useState<DfrData>(initialData);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // AI API Mode: 'direct' (Client-Side) or 'backend' (Server-Side Agents)
    const [apiMode, setApiMode] = useState<'direct' | 'backend'>('backend'); // Default to backend as requested standard

    // Data Source Mode: 'firestore' or 'local'
    // Now we support 'backend' (file system via API) as the source of truth
    const [dataSourceMode, setDataSourceMode] = useState<'firestore' | 'local' | 'backend'>('backend');

    // Sync Status State
    const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error' | 'offline'>('synced');
    const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

    // Project Management
    const [activeProject, setActiveProject] = useState<string>(() => {
        return localStorage.getItem('magnus_active_project') || 'all';
    });
    useEffect(() => {
        localStorage.setItem('magnus_active_project', activeProject);
    }, [activeProject]);


    // --- Subscription Management ---
    const [subscriptionConfig, setSubscriptionConfig] = useState<any>(() => {
        const saved = localStorage.getItem('MAGNUS_SUBSCRIPTION_CONFIG');
        return saved ? JSON.parse(saved) : null;
    });

    const updateSubscriptionConfig = useCallback((config: any) => {
        setSubscriptionConfig(config);
        localStorage.setItem('MAGNUS_SUBSCRIPTION_CONFIG', JSON.stringify(config));
        // console.log("✅ Subscription Configuration Updated");
    }, []);

    const hasFeature = useCallback((featureId: string): boolean => {
        // 1. Magnus (platform owner) always has access - but NOT autoGainAdmin so they can test
        if (currentUser?.role === 'magnus') return true;

        // 2. Default if no config exists (Avoid locking out before config is saved)
        if (!subscriptionConfig || !subscriptionConfig.matrix) return true;

        // 3. Determine User Plan based on role/organization
        // autoGainAdmin and admin roles map to 'enterprise'
        // Other users default to 'standard' unless they have a plan property
        const isEnterprise = currentUser?.organization === 'AutoGain' ||
            currentUser?.role === 'admin' ||
            currentUser?.role === 'autoGainAdmin' ||
            currentUser?.role === 'clientAdmin';

        const userPlan = isEnterprise ? 'enterprise' : ((currentUser as any)?.plan || 'standard');

        // 4. Check Matrix
        const planFeatures = subscriptionConfig.matrix[userPlan];
        if (!planFeatures) {
            // Plan not found in matrix - allow access to prevent lockout
            return true;
        }

        return !!planFeatures[featureId];
    }, [currentUser, subscriptionConfig]);

    // Automatically derive visual project list from iteration history
    const projectList = useMemo(() => {
        const projectMap = new Map<string, Set<number>>();
        if (allData.iterationHistory) {
            allData.iterationHistory.forEach((ep) => {
                let name = ep.projectName;
                if (!name) {
                    name = extractProjectName(ep.epochName || ep.iterationName || '');
                }
                if (name && name !== 'Unknown Project' && name.trim() !== '') {
                    if (!projectMap.has(name)) projectMap.set(name, new Set());
                    const id = ep.epochNumber || ep.iterationNumber || ep.iteration || 0;
                    projectMap.get(name)!.add(id);
                }
            });
        }

        // Also include active project if it's not in history yet
        if (allData.projectMetadata?.name && !projectMap.has(allData.projectMetadata.name)) {
            projectMap.set(allData.projectMetadata.name, new Set([allData.projectMetadata.epochId || 0]));
        }

        return Array.from(projectMap.entries()).map(([name, epochs]) => ({
            name,
            epochIds: Array.from(epochs).sort((a, b) => a - b)
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [allData.iterationHistory, allData.projectMetadata]);

    const routerNavigate = useNavigate();

    // PRIMARY SAVE FUNCTION: Writes to Backend (Truth) ONLY
    const saveToBackendAndCache = useCallback(async (newData: DfrData) => {
        setSyncStatus('syncing');

        // Validate first
        const validation = validateBeforeSave(newData);
        if (!validation.valid) {
            console.error('❌ Data validation FAILED:', validation.errors);
            // Optionally throw or alert here, but we'll try to save anyway or abort? 
            // Aborting might be safer for strict mode.
        }

        // 1. Save to Backend API (WITH AUTHENTICATION)
        try {
            await authenticatedFetchJSON('/api/projects/save', {
                method: 'POST',
                body: JSON.stringify(newData)
            });

            // Success
            setSyncStatus('synced');
            setLastSyncTime(new Date());
            setDataSourceMode('backend');
            console.log('✅ Saved to backend with authentication');

        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('❌ Save to Backend FAILED:', error);
            setSyncStatus('error');

            // Show user-friendly error
            if (message.includes('Permission denied')) {
                alert("⛔ PERMISSION DENIED: You do not have permission to save projects.\n\nRequired permission: project.write (engineer, org_admin, or platform_admin)");
            } else if (message.includes('Not authenticated') || message.includes('Authentication token expired')) {
                alert("🔐 SESSION EXPIRED: Your authentication session has expired.\n\nPlease sign in again to continue.");
                window.location.href = '/admin/login';
            } else {
                alert(`❌ SAVE FAILED: ${message}\n\nYour changes are NOT saved persistently.`);
            }
        }
    }, []);

    const clearCache = useCallback(() => {
        localStorage.removeItem('magnus_dfr_data');
        console.log('🗑️ Cache cleared');
    }, []);

    const loadMockDataToBackend = useCallback(async () => {
        try {
            await authenticatedFetchJSON('/api/projects/load-mock-data', { method: 'POST' });
            window.location.reload();
        } catch (error) {
            console.error(error);
        }
    }, []);

    const refreshData = useCallback(async () => {
        setIsLoading(true);
        try {
            setSyncStatus('syncing');

            // 1. Try Load from Backend API (PUBLIC endpoint - no auth required)
            const rawName = activeProject && (activeProject as string) !== 'all' ? activeProject : 'Aerator Pro';
            const sanitizedName = rawName.replace(/ /g, '_');
            const encodedName = encodeURIComponent(sanitizedName);

            console.log(`🔍 Attempting to load project: ${sanitizedName}`);

            try {
                // Use authenticated /load endpoint (RBAC requires auth)
                const backendData = await authenticatedFetchJSON(`/api/projects/load/${encodedName}`);

                // Promotion logic for legacy data
                let promotedFmeaItems = backendData.fmeaItems || [];
                if (promotedFmeaItems.length === 0 && (backendData.iterationHistory || backendData.epochs)) {
                    const history = backendData.iterationHistory || backendData.epochs || [];
                    const allEpochItems: any[] = [];
                    history.forEach((epoch: IterationRecord) => {
                        if (epoch.fmeaItems && Array.isArray(epoch.fmeaItems)) {
                            epoch.fmeaItems.forEach((item) => {
                                allEpochItems.push({
                                    ...item,
                                    epochId: item.epochId || (epoch.epochNumber || epoch.iteration)
                                });
                            });
                        }
                    });
                    const latestItems = new Map<string, any>();
                    allEpochItems.forEach(item => latestItems.set(item.id, item));
                    promotedFmeaItems = Array.from(latestItems.values());
                }

                const fullData = {
                    ...initialData,
                    ...backendData,
                    fmeaItems: promotedFmeaItems.length > 0 ? promotedFmeaItems : (backendData.fmeaItems || []),
                };

                setAllData(fullData);
                localStorage.setItem('magnus_dfr_data', JSON.stringify(fullData));
                setSyncStatus('synced');
                console.log(`✅ Loaded project: ${rawName} (${fullData.fmeaItems?.length || 0} FMEA items)`);

            } catch (error) {
                console.error('❌ Backend load FAILED:', error);
                console.error('   → This means the app cannot connect to the Firebase/Cloud Run backend');
                console.error('   → Showing empty data - NO cache fallback used');

                // NO CACHE FALLBACK - Show empty data so connection issues are obvious
                // Previously we would load from localStorage, but that hides connection problems
                setAllData(initialData);
                setSyncStatus('error');
            }

            // Safe call to refreshBackendProjects if it exists in scope (hoisting issue potential but unlikely with const in same scope)
            // To be safe, we might move this call or accept the lint warning.
            // checking if function exists before call
            // refreshBackendProjects(); // Commented out to avoid reference error if defined below

        } catch (error) {
            console.error("Failed to refresh data", error);
            setAllData(initialData);
            setSyncStatus('error');
        } finally {
            setIsLoading(false);
        }
    }, [saveToBackendAndCache, activeProject]);

    const [backendProjects, setBackendProjects] = useState<string[]>([]);

    // Controls Library Management
    const refreshControls = useCallback(async () => {
        try {
            const response = await authenticatedFetchJSON('/api/controls/');
            console.log(`✅ Loaded ${response.length} controls from Controls Library`);

            // Merge with local controls (if any specific to project?)
            // For now, let's treat the backend library as the master source for the "Library"
            // But we might want to keep project-specific controls in 'allData.controls' ?
            // The request says "make sure the data for this controls library is only from the backend"
            // So we should override or separate them.
            // Let's update allData.controls with these values.

            setAllData(prev => ({
                ...prev,
                controls: response
            }));

        } catch (error) {
            console.warn('Failed to fetch controls library:', error);
        }
    }, []);

    const refreshBackendProjects = useCallback(async () => {
        try {
            // Use authenticated /api/projects/ endpoint (RBAC-enforced, org-scoped)
            const projectFiles = await authenticatedFetchJSON('/api/projects/');
            const names = projectFiles.map((file: { filename?: string; name?: string }) =>
                file.name || (file.filename ? file.filename.replace('.json', '') : 'Unknown')
            );
            setBackendProjects(names);
            console.log(`✅ Loaded ${names.length} projects from backend`);
        } catch (error: any) {
            console.warn('⚠️ Failed to fetch projects:', error.message || error);
        }
    }, []);

    // CROSS-APP SYNC: Listen for project changes from Magnus Studio
    // NOTE: Must be after refreshData and refreshBackendProjects definitions
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'magnus_active_project' && e.newValue && e.newValue !== activeProject) {
                console.log(`[SYNC] Active project changed externally to: ${e.newValue}`);
                setActiveProject(e.newValue);
                refreshData();
                refreshBackendProjects();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [activeProject, refreshData, refreshBackendProjects]);

    useEffect(() => {
        // Users are managed via Firebase Auth. We no longer use magnus_users local storage.
        setUsers([]);
    }, []);

    const checkStorageMode = useCallback(async () => {
        try {
            // Use authenticated fetch for storage mode endpoint
            const config = await authenticatedFetchJSON('/api/projects/config/storage-mode');
            if (config.mode === 'firestore') {
                setDataSourceMode('firestore');
                console.log('🔥 Backend reports Firestore is ENABLED');
            } else {
                console.log('📁 Backend reports LOCAL mode');
            }
        } catch (e) {
            console.warn('Failed to check storage mode (backend may be offline)', e);
        }
    }, []);

    // Load data on mount and when data source mode or user changes
    useEffect(() => {
        if (!currentUser) {
            setIsLoading(false);
            return;
        }
        checkStorageMode();
        refreshData();
        refreshBackendProjects();
        refreshControls();
    }, [refreshData, refreshBackendProjects, refreshControls, dataSourceMode, currentUser, checkStorageMode]);

    // Function to switch data source mode
    const switchDataSource = (mode: 'firestore' | 'local' | 'backend') => {
        console.log(`🔄 Switching data source to: ${mode.toUpperCase()} `);
        setDataSourceMode(mode);
        refreshData(); // Trigger data reload for new source
    };

    const login = (username: string, password?: string): User | null => {
        throw new Error('Email/password login is deprecated. Use Google Sign-In via authentication flow.');
    };

    // ISO 27001 Gatekeeper: Handle Firebase Auth & User Provisioning
    const handleFirebaseUser = async (firebaseUser: any) => {
        if (!firebaseUser) {
            console.log('🔓 No Firebase session');
            setCurrentUser(null);
            setIsAuthLoading(false);
            return;
        }

        const email = firebaseUser.email || '';
        console.log('🔄 Verifying Access for:', email);
        const db = getFirestore();
        const userRef = doc(db, 'users', firebaseUser.uid);

        try {
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                // Existing User: Check Access Rights
                const userData = userSnap.data() as User;

                // Fallback for owner if DB was manually wiped but doc exists (rare)
                const isOwner = email.toLowerCase() === 'wayneautogain@gmail.com';
                if (isOwner && userData.role !== 'autoGainAdmin') {
                    // Self-heal owner role if needed
                }

                // =========================================================
                // ISO 27001 CRITICAL SECURITY: Check Firestore approval
                // Mirrors the isApproved check in Magnus-PFMEA AuthContext.tsx
                // Users must be explicitly approved by an admin before accessing data
                // =========================================================
                if (userData.isApproved || userData.role === 'autoGainAdmin' || isOwner) {
                    // A.9.2.5 - Review of Access Rights (Tracking)
                    // Fire-and-forget update of lastLogin timestamp
                    updateDoc(userRef, { lastLogin: serverTimestamp() }).catch(e => console.warn("Failed to update login time", e));

                    // ISO 27001 - Secure Audit Logging for Admin/User Logins
                    authenticatedFetchJSON('/api/users/log-login', { method: 'POST' })
                        .catch(e => console.warn("Failed to audit log login", e));

                    console.log('✅ Access Granted:', userData.role);
                    setCurrentUser({
                        ...userData,
                        uid: firebaseUser.uid,
                        username: userData.username || firebaseUser.displayName || 'User',
                        email: email, // Ensure email is fresh from auth token
                        // Allow specific overrides but generally trust DB permissions
                    });
                } else {
                    // Access Denied / Pending
                    console.warn(`⛔ Access Restricted: User ${email} is pending approval.`);
                    setCurrentUser({
                        uid: firebaseUser.uid,
                        username: userData.username || firebaseUser.displayName || 'Pending User',
                        email: email,
                        role: 'pending', // Force pending role
                        permissions: [], // ZERO permissions
                        isApproved: false
                    });
                }
            } else {
                // A.9.2.1 User Registration (Self-Service)
                // Determine if this is the Platform Owner (First-run bootstrap)
                const isOwner = email.toLowerCase() === 'wayneautogain@gmail.com';

                const newUser: User = {
                    uid: firebaseUser.uid,
                    username: firebaseUser.displayName || email.split('@')[0],
                    email: email,
                    role: isOwner ? 'autoGainAdmin' : 'pending', // Default Deny for everyone else
                    permissions: isOwner ? ['*'] : [],
                    isApproved: isOwner,
                    organization: isOwner ? 'AutoGain' : 'Unknown',
                    initials: (firebaseUser.displayName || email).substring(0, 2).toUpperCase(),
                    ecnCanCreate: isOwner,
                    ecnCanApprove: isOwner
                };

                // Create persistent profile
                // We use defaults for timestamps here, real serverTimestamp will apply on write
                await setDoc(userRef, {
                    ...newUser,
                    createdAt: serverTimestamp(),
                    lastLogin: serverTimestamp(),
                    clientId: isOwner ? 'autogain-internal' : null
                });

                console.log(`✨ Registered User: ${email} (Role: ${newUser.role})`);

                // ISO 27001 - Secure Audit Logging for First Time Registration/Login
                authenticatedFetchJSON('/api/users/log-login', { method: 'POST' })
                    .catch(e => console.warn("Failed to audit log first login", e));

                setCurrentUser(newUser);
            }
        } catch (error) {
            console.error('❌ Gatekeeper Error:', error);
            // Fail Secure
            setCurrentUser(null);
        }
        setIsAuthLoading(false);
    };

    // Listen for Firebase Auth state changes
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            handleFirebaseUser(firebaseUser);
        });

        return () => unsubscribe();
    }, []);

    const logout = () => {
        if (currentUser) {
            logAction('logout', 'User logged out');
        }
        setCurrentUser(null);
        routerNavigate('/admin/login');
    };

    const updateUserPermissions = async (username: string, permissions: string[]) => {
        // Obsolete function, permissions should be updated via backend API
        console.warn('updateUserPermissions is deprecated');
    };

    // New function to add user
    const addUser = async (newUser: User) => {
        // Obsolete function
        console.warn('addUser is deprecated');
    }

    const changePassword = async (username: string, newPassword: string) => {
        // Obsolete function
        console.warn('changePassword is deprecated');
    };

    const logAction = useCallback(async (type: 'login' | 'navigation' | 'feature_usage' | 'admin_action' | 'logout', details: string, path?: string) => {
        // Mocked out to replace legacy logActivity
        console.log(`Log Action [${type}]: ${details}`);
    }, []);

    const uploadMockData = async () => {
        // Check if user is admin or magnus
        if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'magnus')) {
            alert('Only administrators can seed the database.');
            return;
        }
        // Redirect to Backend Restore
        if (window.confirm("CONFIRM RESTORE: This will reset the BACKEND database to the default 'Aerator Pro' dataset.\n\nAny unsaved changes in the backend will be lost.\n\nContinue?")) {
            await loadMockDataToBackend();
        }
    };

    const updateFmeaData = async (fmeaItems: FmeaItem[]) => {
        // Always local mode for now
        setAllData(prev => {
            const newData = { ...prev, fmeaItems };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updateFmeaItem = async (fmeaItem: FmeaItem) => {
        setAllData(prev => {
            const items = prev.fmeaItems.map(i => i.id === fmeaItem.id ? fmeaItem : i);
            if (!prev.fmeaItems.find(i => i.id === fmeaItem.id)) items.push(fmeaItem);
            const newData = { ...prev, fmeaItems: items };
            saveToBackendAndCache(newData);
            return newData;
        });
    }

    const addPDiagram = async (pDiagram: PDiagramData) => {
        setAllData(prev => {
            const list = prev.pDiagramList || [];
            // Check for existing diagram with same composite key (Function + Project)
            const index = list.findIndex(d =>
                d.consolidatedFunctionId === pDiagram.consolidatedFunctionId &&
                normalizeProjectName(d.projectName || '') === normalizeProjectName(pDiagram.projectName || '')
            );

            let newList;
            if (index >= 0) {
                // Update existing
                newList = [...list];
                newList[index] = pDiagram;
            } else {
                // Add new
                newList = [...list, pDiagram];
            }

            const newData = { ...prev, pDiagramList: newList };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const deletePDiagram = async (id: string, status: 'active' | 'draft') => {
        setAllData(prev => {
            // Filter by consolidatedFunctionId AND status to remove specific version
            const newData = {
                ...prev,
                pDiagramList: prev.pDiagramList.filter(d =>
                    !(d.consolidatedFunctionId === id && (d.status || 'draft') === status)
                )
            };
            saveToBackendAndCache(newData);
            return newData;
        });
    }

    const addEpoch = async (epoch: IterationRecord) => {
        setAllData(prev => {
            const existingHistory = prev.iterationHistory || [];
            const newData = { ...prev, iterationHistory: [...existingHistory, epoch] };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const deleteEpoch = async (epochNumber: number) => {
        setAllData(prev => {
            const existingHistory = prev.iterationHistory || [];
            const newHistory = existingHistory.filter((e: IterationRecord) => (e.iteration || e.epochNumber) !== epochNumber);

            // Also cleanup any FMEA items associated with this epoch
            const newFmeaItems = prev.fmeaItems.filter(item => item.epochId !== epochNumber);

            const newData = {
                ...prev,
                iterationHistory: newHistory,
                fmeaItems: newFmeaItems
            };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updateBotConfig = async (botConfig: BotConfig) => {
        // Local update
        setAllData(prev => {
            const bots = prev.botConfigs.map(b => b.id === botConfig.id ? botConfig : b);
            const newData = { ...prev, botConfigs: bots };
            saveToBackendAndCache(newData);
            return newData;
        });
        await refreshData();
    }

    useEffect(() => {
        if (highlightedItem) {
            const timer = setTimeout(() => setHighlightedItem(null), 2200);
            return () => clearTimeout(timer);
        }
    }, [highlightedItem]);

    const setTheme = (newTheme: Theme) => setThemeState(newTheme);
    const setViewMode = (mode: ViewMode) => setViewModeState(mode);

    const navigate = useCallback((path: string, options?: { state?: unknown; highlight?: HighlightableItem }) => {
        const currentPath = window.location.hash.substring(1) || '/';

        // Fire and forget - logging failures won't break navigation
        logAction('navigation', `Navigated to ${path}`, path);

        setNavigationHistory(prev => [...prev, { path: currentPath, highlight: highlightedItem ?? undefined }]);
        routerNavigate(path, { state: options?.state });
        if (options?.highlight) {
            setHighlightedItem(options.highlight);
        }
    }, [routerNavigate, highlightedItem, currentUser]);

    const goBack = useCallback(() => {
        setNavigationHistory(prev => {
            const lastState = prev.pop();
            if (lastState) {
                routerNavigate(lastState.path);
                if (lastState.highlight) {
                    setHighlightedItem(lastState.highlight);
                }
            }
            return [...prev];
        });
    }, [routerNavigate]);

    const canGoBack = navigationHistory.length > 0;

    const addCustomer = async (customer: Customer) => {
        setAllData(prev => {
            const newData = { ...prev, customers: [...prev.customers, customer] };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const addCustomerRequirement = async (requirement: CustomerRequirement) => {
        setAllData(prev => {
            const newData = { ...prev, customerRequirements: [...prev.customerRequirements, requirement] };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updateProjectContext = async (metadata: ProjectMetadata, context: ProjectContextData) => {
        setAllData(prev => {
            const newData = { ...prev, projectMetadata: metadata, projectContext: context };
            // Also ensure persistence
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    // --- PBS Manager Pro ---
    const createPbsRevision = async (revision: PbsRevisionLevel) => {
        setAllData(prev => {
            // 1. Add Revision Level
            const existingRevisions = prev.pbsRevisions || [];
            // Deactivate other revisions if this one is active? 
            // Usually we might want to keep history, but if status is active, ensure only one active?
            // Metadata rules say: "Validate only one active revision per project"
            let updatedRevisions = [...existingRevisions];
            if (revision.isActive) {
                updatedRevisions = updatedRevisions.map(r =>
                    (r.projectName === revision.projectName && r.isActive)
                        ? { ...r, isActive: false } as PbsRevisionLevel
                        : r
                );
            }
            updatedRevisions.push(revision);

            // 2. Auto-generate ECN (Metadata Standards v2.0.0)
            const ecnLog = prev.ecnLog || [];
            const newEcn: ECNRecord = {
                ecnNumber: `ECN-PBS-${Date.now().toString(36).toUpperCase()}`,
                ecnTitle: `PBS Revision Creation: ${revision.name}`,
                ecnType: 'Design Change',
                priority: 'Medium',
                status: 'Draft',
                submittedBy: currentUser?.initials || 'SYS',
                submittedDate: new Date().toISOString().split('T')[0],
                reviewedBy: [],
                approvedBy: '',
                approvedDate: '',
                implementedDate: '',
                changeDescription: `Creation of new PBS Revision Level: ${revision.name} (${revision.stage}). ${revision.description}`,
                reasonForChange: 'New design maturity level established.',
                affectedDocuments: ['PBS', 'BOM'],
                affectedItems: [], // Will be populated as items are added/modified
                previousConfiguration: revision.carriedOverFrom || 'N/A',
                newConfiguration: revision.name,
                costImpact: { tooling: 0, material: 0, labor: 0, total: 0 },
                scheduleImpact: 'None',
                qualityImpact: 'None',
                validationRequired: false,
                testingRequired: [],
                approvalHistory: [],

                // Metadata Links
                projectName: revision.projectName,
                epochId: (revision as any).epochId || 0,
                pbsRevisionId: revision.id,
                pbsRevisionName: revision.name,
                pbsChangeType: 'added'
            };

            const newData = {
                ...prev,
                pbsRevisions: updatedRevisions,
                ecnLog: [...ecnLog, newEcn]
            };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updatePbsItem = async (item: PbsItemRevision) => {
        setAllData(prev => {
            const items = prev.pbsItems || [];
            const otherItems = items.filter(i => i.id !== item.id);
            const nextPbsItems = [...otherItems, item];

            let nextBomItems = prev.bomItems || [];
            // Sync to Master BOM if linked
            if (item.bomItemId) {
                nextBomItems = nextBomItems.map(b => b.id === item.bomItemId ? {
                    ...b,
                    component: item.name,
                    partNumber: item.partNumber,
                    description: item.comment || b.description
                } : b);
            }

            const newData = { ...prev, pbsItems: nextPbsItems, bomItems: nextBomItems };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updateBomItem = async (item: BomItem) => {
        setAllData(prev => {
            const items = prev.bomItems || [];
            const exists = items.find(i => i.id === item.id);
            let nextBomItems;
            if (exists) {
                nextBomItems = items.map(i => i.id === item.id ? item : i);
            } else {
                nextBomItems = [...items, item];
            }

            // Sync to all PBS Item revisions that reference this BOM item
            const nextPbsItems = (prev.pbsItems || []).map(p =>
                p.bomItemId === item.id ? {
                    ...p,
                    name: item.component,
                    partNumber: item.partNumber || p.partNumber,
                    comment: item.description || p.comment
                } : p
            );

            const newData = { ...prev, bomItems: nextBomItems, pbsItems: nextPbsItems };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const deleteBomItem = async (id: number) => {
        setAllData(prev => {
            const newData = { ...prev, bomItems: (prev.bomItems || []).filter(i => i.id !== id) };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const updateEcnLog = async (ecnLog: ECNRecord[]) => {
        setAllData(prev => {
            const newData = { ...prev, ecnLog };
            saveToBackendAndCache(newData);
            return newData;
        });
    };

    const approveWorkflowStep = async (stepId: string, notes?: string) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const response = await authenticatedFetchJSON(`/api/projects/${filename}/workflow/approve`, {
                method: 'POST',
                body: JSON.stringify({ stepId, approverInitials: currentUser?.initials || 'SYS', notes })
            });
            console.log("✅ Workflow approval successful:", response);
            await refreshData();
        } catch (error) {
            console.error("❌ Workflow approval failed:", error);
            alert("Workflow approval failed. See console for details.");
        }
    };

    const approveRequirement = async (reqId: string, notes?: string) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            await authenticatedFetchJSON(`/api/projects/${filename}/requirements/${reqId}/approve`, {
                method: 'POST',
                body: JSON.stringify({ approverInitials: currentUser?.initials || 'SYS', notes })
            });
            // Update local state for immediate feedback
            setAllData(prev => ({
                ...prev,
                customerRequirements: prev.customerRequirements.map(r =>
                    r.id === reqId ? { ...r, status: 'Approved' } : r
                )
            }));
            console.log(`✅ Requirement ${reqId} approved`);
        } catch (error) {
            console.error("❌ Requirement approval failed:", error);
        }
    };

    const exportProject = async (format: 'csv' | 'json' | 'pdf' = 'csv'): Promise<string> => {
        try {
            const projectName = activeProject && activeProject !== 'all' ? activeProject : 'project_export';

            if (format === 'csv') {
                // --- Client-side CSV export (no backend required) ---
                const escape = (val: unknown): string => {
                    const s = val === null || val === undefined ? '' : String(val);
                    return s.includes(',') || s.includes('"') || s.includes('\n')
                        ? `"${s.replace(/"/g, '""')}"`
                        : s;
                };

                const headers = [
                    'ID', 'Project', 'Function', 'Requirement', 'Failure Mode',
                    'Effects', 'S', 'O', 'D', 'RPN', 'Action Priority',
                    'Prevention Controls', 'Detection Controls', 'Recommended Action',
                    'Action Responsibility', 'Action Target Date', 'Action Taken', 'Action Completion Date', 'Status'
                ];

                const rows = (allData.fmeaItems || []).map(item => {
                    const s = Number(item.S) || 0;
                    const o = Number(item.O) || 0;
                    const d = Number(item.D) || 0;
                    const toStr = (v: unknown) => Array.isArray(v) ? (v as string[]).join('; ') : String(v ?? '');
                    return [
                        escape(item.id),
                        escape(item.projectName || projectName),
                        escape(item.function),
                        escape(item.requirement),
                        escape(item.failureMode),
                        escape(item.effects),
                        escape(s),
                        escape(o),
                        escape(d),
                        escape(s * o * d),
                        escape(item.actionPriority || 'L'),
                        escape(toStr(item.preventionControls)),
                        escape(toStr(item.detectionControls)),
                        escape(item.recommendedAction),
                        escape(item.actionResponsibility),
                        escape(item.actionTargetDate),
                        escape(item.actionTaken),
                        escape(item.actionCompletionDate),
                        escape(item.status || 'active'),
                    ].join(',');
                });

                const headersStr = headers.join(',');

                const metadata: ExportAuditMetadata = {
                    exporterUid: currentUser?.uid || 'unknown',
                    organization: (allData.projectMetadata as any)?.organization || 'unknown',
                    timestamp: new Date().toISOString()
                };

                downloadCSVWithAudit(`${projectName}_FMEA_export.csv`, headersStr, rows, metadata);
                console.log(`✅ Exported ${(allData.fmeaItems || []).length} FMEA items as CSV`);
                return ''; // download triggered inline; no URL needed

            } else if (format === 'json') {
                // Client-side JSON export
                const metadata: ExportAuditMetadata = {
                    exporterUid: currentUser?.uid || 'unknown',
                    organization: (allData.projectMetadata as any)?.organization || 'unknown',
                    timestamp: new Date().toISOString()
                };

                const exportData = wrapWithAuditMetadata(allData, metadata);
                const json = JSON.stringify(exportData, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${projectName}_export.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                console.log(`✅ Exported project as JSON`);
                return '';

            } else {
                // For PDF and other formats, fall back to backend
                const response = await authenticatedFetchJSON<any>(`/api/projects/${projectName}/export?format=${format}`);
                return response.downloadUrl;
            }
        } catch (error) {
            console.error("❌ Export failed:", error);
            throw error;
        }
    };

    const logTestResult = async (testId: string, result: 'PASS' | 'FAIL' | 'MARGINAL', measuredValue?: string, units?: string, notes?: string) => {
        try {
            await authenticatedFetchJSON(`/api/v1/lab/tests/${testId}/log-result`, {
                method: 'POST',
                body: JSON.stringify({
                    result,
                    measuredValue,
                    units,
                    operatorInitials: currentUser?.initials || 'SYS',
                    notes
                })
            });
            console.log(`✅ Test ${testId} result logged: ${result}`);
        } catch (error) {
            console.error("❌ Test logging failed:", error);
        }
    };

    const createControl = async (control: any) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const response = await authenticatedFetchJSON<any>(`/api/projects/${filename}/controls`, {
                method: 'POST',
                body: JSON.stringify(control)
            });
            setAllData(prev => ({
                ...prev,
                controls: [...prev.controls, response.control]
            }));
            console.log("✅ Control created:", response.control);
        } catch (error) {
            console.error("❌ Control creation failed:", error);
        }
    };

    const createDvprTest = async (test: any) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const response = await authenticatedFetchJSON<any>(`/api/projects/${filename}/dvpr`, {
                method: 'POST',
                body: JSON.stringify(test)
            });
            setAllData(prev => ({
                ...prev,
                dvprTests: [...prev.dvprTests, response.test]
            }));
            console.log("✅ DVPR test created:", response.test);
        } catch (error) {
            console.error("❌ DVPR test creation failed:", error);
        }
    };

    const runDvprAiAnalysis = async (): Promise<string> => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const response = await authenticatedFetchJSON<any>(`/api/projects/${filename}/dvpr/ai-analysis`, {
                method: 'POST'
            });
            return response.insight;
        } catch (error) {
            console.error("❌ DVPR AI Analysis failed:", error);
            return "Failed to run AI analysis.";
        }
    };

    // --- Products & Customers Management ---

    const updateProduct = async (product: Product) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const exists = (allData.products || []).find(p => p.id === product.id);
            const method = exists ? 'PUT' : 'POST';
            const url = exists
                ? `/api/projects/${filename}/products/${product.id}`
                : `/api/projects/${filename}/products`;

            const response = await authenticatedFetchJSON<any>(url, {
                method,
                body: JSON.stringify(product)
            });

            const savedProduct = response.product || product;
            setAllData(prev => {
                const products = prev.products || [];
                const newProducts = exists
                    ? products.map(p => p.id === savedProduct.id ? savedProduct : p)
                    : [...products, savedProduct];
                return { ...prev, products: newProducts };
            });
            console.log(`✅ Product ${exists ? 'updated' : 'created'}:`, savedProduct.name);
        } catch (error) {
            console.error("❌ Product update failed:", error);
            alert("Failed to save product. See console for details.");
        }
    };

    const deleteProduct = async (productId: string) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            await authenticatedFetchJSON(`/api/projects/${filename}/products/${productId}`, {
                method: 'DELETE'
            });

            setAllData(prev => ({
                ...prev,
                products: (prev.products || []).filter(p => p.id !== productId)
            }));
            console.log(`✅ Product deleted:`, productId);
        } catch (error) {
            console.error("❌ Product deletion failed:", error);
            alert("Failed to delete product. See console for details.");
        }
    };

    const updateProductFeature = async (feature: ProductFeature) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const exists = (allData.productFeatures || []).find(f => f.id === feature.id);
            const method = exists ? 'PUT' : 'POST';
            const url = exists
                ? `/api/projects/${filename}/product-features/${feature.id}`
                : `/api/projects/${filename}/product-features`;

            const response = await authenticatedFetchJSON<any>(url, {
                method,
                body: JSON.stringify(feature)
            });

            const savedFeature = response.feature || feature;
            setAllData(prev => {
                const features = prev.productFeatures || [];
                const newFeatures = exists
                    ? features.map(f => f.id === savedFeature.id ? savedFeature : f)
                    : [...features, savedFeature];
                return { ...prev, productFeatures: newFeatures };
            });
            console.log(`✅ Feature ${exists ? 'updated' : 'created'}:`, savedFeature.name);
        } catch (error) {
            console.error("❌ Feature update failed:", error);
            alert("Failed to save feature. See console for details.");
        }
    };

    const deleteProductFeature = async (featureId: string) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            await authenticatedFetchJSON(`/api/projects/${filename}/product-features/${featureId}`, {
                method: 'DELETE'
            });

            setAllData(prev => ({
                ...prev,
                productFeatures: (prev.productFeatures || []).filter(f => f.id !== featureId)
            }));
            console.log(`✅ Feature deleted:`, featureId);
        } catch (error) {
            console.error("❌ Feature deletion failed:", error);
            alert("Failed to delete feature.");
        }
    };

    const updateCustomer = async (customer: Customer) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            const exists = (allData.customers || []).find(c => c.id === customer.id);
            const method = exists ? 'PUT' : 'POST';
            const url = exists
                ? `/api/projects/${filename}/customers/${customer.id}`
                : `/api/projects/${filename}/customers`;

            const response = await authenticatedFetchJSON<any>(url, {
                method,
                body: JSON.stringify(customer)
            });

            const savedCustomer = response.customer || customer;
            setAllData(prev => {
                const customers = prev.customers || [];
                const newCustomers = exists
                    ? customers.map(c => c.id === savedCustomer.id ? savedCustomer : c)
                    : [...customers, savedCustomer];
                return { ...prev, customers: newCustomers };
            });
            console.log(`✅ Customer ${exists ? 'updated' : 'created'}:`, savedCustomer.name);
        } catch (error) {
            console.error("❌ Customer update failed:", error);
            alert("Failed to save customer.");
        }
    };

    const deleteCustomer = async (customerId: string) => {
        try {
            const filename = activeProject && activeProject !== 'all' ? activeProject : 'Aerator_Pro';
            await authenticatedFetchJSON(`/api/projects/${filename}/customers/${customerId}`, {
                method: 'DELETE'
            });

            setAllData(prev => ({
                ...prev,
                customers: (prev.customers || []).filter(c => c.id !== customerId)
            }));
            console.log(`✅ Customer deleted:`, customerId);
        } catch (error) {
            console.error("❌ Customer deletion failed:", error);
            alert("Failed to delete customer.");
        }
    };

    const importProjectData = async (projectData: DfrData, replace: boolean = false) => {
        setIsLoading(true);
        try {
            // Backend will handle metadata migration when the project is saved

            // NORMALIZE DATA STRUCTURE: Handle legacy formats from backend
            const rawIterationHistory = projectData.iterationHistory || (projectData as unknown as { epochs: IterationRecord[] }).epochs || [];
            const rawFmeaItems = projectData.fmeaItems || [];
            const rawPDiagramList = projectData.pDiagramList || [];

            const loadedProjectName = projectData.projectMetadata?.name || 'Imported Project';

            // Apply Metadata Rules during Import
            const normalizedHistory = rawIterationHistory.map((e: any) => ({
                ...e,
                projectName: e.projectName || loadedProjectName
            }));

            const normalizedFmeaItems = rawFmeaItems.map((i: any) => ({
                ...i,
                projectName: i.projectName || loadedProjectName
            }));

            const normalizedPDiagrams = rawPDiagramList.map((d: any) => ({
                ...d,
                projectName: d.projectName || loadedProjectName
            }));

            // PROMOTION LOGIC: If root fmeaItems is empty, look into the latest epoch or all epochs
            let promotedFmeaItems = normalizedFmeaItems;
            if (promotedFmeaItems.length === 0 && normalizedHistory.length > 0) {
                console.log('🔍 No root FMEA items found. Extracting from epochs...');
                const allEpochItems: FmeaItem[] = [];
                normalizedHistory.forEach((epoch: IterationRecord) => {
                    if (epoch.fmeaItems && Array.isArray(epoch.fmeaItems)) {
                        epoch.fmeaItems.forEach((item) => {
                            allEpochItems.push({
                                ...item,
                                projectName: item.projectName || loadedProjectName,
                                epochId: item.epochId || (epoch.epochNumber || epoch.iteration)
                            } as FmeaItem);
                        });
                    }
                });

                // Use the latest version of each item ID
                const latestItems = new Map<string, any>();
                allEpochItems.forEach(item => latestItems.set(item.id, item));
                promotedFmeaItems = Array.from(latestItems.values());
                console.log(`✅ Promoted ${promotedFmeaItems.length} FMEA items from epochs.`);
            }

            setAllData(prev => {
                let mergedHistory, mergedItems, mergedDiagrams;

                if (replace) {
                    // Full overwrite for "Open Project" behavior
                    mergedHistory = normalizedHistory;
                    mergedItems = promotedFmeaItems;
                    mergedDiagrams = normalizedPDiagrams;
                } else {
                    // 1. Merge Iteration History (Epochs) - check for project + ID uniqueness
                    const existingEpochKeys = new Set(prev.iterationHistory.map((e: any) =>
                        `${normalizeProjectName(e.projectName || 'Other')} - ${e.epochNumber || e.iteration}`
                    ));
                    const newEpochs = normalizedHistory.filter((e: any) =>
                        !existingEpochKeys.has(`${normalizeProjectName(e.projectName || 'Other')} - ${e.epochNumber || e.iteration}`)
                    );
                    mergedHistory = [...prev.iterationHistory, ...newEpochs];

                    // 2. Merge FMEA Items
                    const existingItemIds = new Set(prev.fmeaItems.map(i => i.id));
                    const newItems = promotedFmeaItems.filter((i: any) => !existingItemIds.has(i.id));
                    mergedItems = [...prev.fmeaItems, ...newItems];

                    // 3. Merge P-Diagrams
                    const existingDiagramIds = new Set(prev.pDiagramList.map(d => d.consolidatedFunctionId));
                    const newDiagrams = normalizedPDiagrams.filter((d: any) => !existingDiagramIds.has(d.consolidatedFunctionId));
                    mergedDiagrams = [...prev.pDiagramList, ...newDiagrams];
                }

                // 4. Construct New Data State
                const newData: DfrData = {
                    ...prev,
                    projectMetadata: replace ? projectData.projectMetadata : (prev.projectMetadata || projectData.projectMetadata),
                    projectContext: replace ? projectData.projectContext : (prev.projectContext || projectData.projectContext),
                    customerRequirements: replace ? (projectData.customerRequirements || []) : [...(prev.customerRequirements || []), ...(projectData.customerRequirements || [])],
                    iterationHistory: mergedHistory,
                    fmeaItems: mergedItems,
                    pDiagramList: mergedDiagrams,
                    
                    // Unified Mechatronic Schema (v2.0.0)
                    requirementChain: replace ? (projectData.requirementChain || []) : (projectData.requirementChain || prev.requirementChain),
                    opalInsight: replace ? (projectData.opalInsight || {}) : (projectData.opalInsight || prev.opalInsight),
                    derivedFunctions: replace ? (projectData.derivedFunctions || []) : (projectData.derivedFunctions || prev.derivedFunctions),
                    auditReportMd: replace ? (projectData.auditReportMd || "") : (projectData.auditReportMd || prev.auditReportMd),
                    systemPiScore: replace ? (projectData.systemPiScore || 0) : (projectData.systemPiScore || prev.systemPiScore),
                    cadqueryScript: replace ? (projectData.cadqueryScript || "") : (projectData.cadqueryScript || prev.cadqueryScript),
                    shapes: replace ? (projectData.shapes || []) : (projectData.shapes || prev.shapes),
                    contacts: replace ? (projectData.contacts || []) : (projectData.contacts || prev.contacts),
                    performanceMetrics: replace ? (projectData.performanceMetrics || []) : (projectData.performanceMetrics || prev.performanceMetrics),

                    // Handle other collections
                    bomItems: replace ? (projectData.bomItems || []) : [...(prev.bomItems || []), ...(projectData.bomItems || []).map((i: any) => ({ ...i, projectName: i.projectName || loadedProjectName }))],
                    controls: replace ? (projectData.controls || []) : [...(prev.controls || []), ...(projectData.controls || []).map((i: any) => ({ ...i, projectName: i.projectName || loadedProjectName }))],
                    dvprTests: replace ? (projectData.dvprTests || []) : [...(prev.dvprTests || []), ...(projectData.dvprTests || []).map((i: any) => ({ ...i, projectName: i.projectName || loadedProjectName }))],
                    materials: replace ? (projectData.materials || []) : [...(prev.materials || []), ...(projectData.materials || []).map((i: any) => ({ ...i, projectName: i.projectName || loadedProjectName }))],
                    productFeatures: replace ? (projectData.productFeatures || []) : [...(prev.productFeatures || []), ...(projectData.productFeatures || []).map((i: any) => ({ ...i, projectName: i.projectName || loadedProjectName }))],
                };

                // Add schema version for metadata compliance
                newData.projectMetadata = {
                    ...newData.projectMetadata,
                    schemaVersion: "2.0.0"
                };

                // 5. Persist
                saveToBackendAndCache(newData);

                console.log(`✅ ${replace ? 'Loaded' : 'Merged'} project "${loadedProjectName}": ${mergedHistory.length} epochs, ${mergedItems.length} FMEA items`);
                return newData;
            });

            // Auto-switch to the project we just loaded
            setActiveProject(loadedProjectName);

        } catch (error) {
            console.error(`Failed to ${replace ? 'load' : 'merge'} project data`, error);
            alert(`Failed to ${replace ? 'load' : 'merge'} project data.`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AppContext.Provider value={{
            theme,
            setTheme,
            viewMode,
            setViewMode,
            highlightedItem,
            navigate,
            goBack,
            canGoBack,
            isHelpOpen,
            setHelpOpen,
            allData,

            // Backend-First Persistence State
            syncStatus,
            lastSyncTime,

            // Helper Functions
            clearCache,
            loadMockDataToBackend,

            masterFunctions: MASTER_FUNCTIONS,
            functionColorMap: functionColorMap,
            isLoading,
            uploadMockData,
            updateFmeaData,
            updateFmeaItem,
            addPDiagram,
            deletePDiagram,
            refreshData,
            dataSourceMode,
            switchDataSource,
            currentUser,
            users,
            login,
            logout,
            updateUserPermissions,
            updateBotConfig,
            addUser,
            addEpoch,
            deleteEpoch,
            addCustomer,
            addCustomerRequirement,
            updateProjectContext,
            changePassword,
            logAction,
            apiMode,
            setApiMode,

            // Subscription Management
            subscriptionConfig,
            updateSubscriptionConfig,
            hasFeature,

            activeProject,
            setActiveProject,
            importProjectData,
            projectList,
            backendProjects,
            refreshBackendProjects,
            createPbsRevision,
            updatePbsItem,
            updateBomItem,
            deleteBomItem,
            updateEcnLog,
            // Functional Contracts
            approveWorkflowStep,
            approveRequirement,
            exportProject,
            logTestResult,
            createControl,
            createDvprTest,
            runDvprAiAnalysis,
            updateProduct,
            deleteProduct,
            updateProductFeature,
            deleteProductFeature,
            updateCustomer,
            deleteCustomer,
            isFullScreen,
            setIsFullScreen,
        } as AppContextType}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): AppContextType => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
